import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { IORGDataTableQueryState } from 'shared-it-appmod-ui';
import Moment from 'src/libraries/moment.library';
import { useRequestContext } from 'src/contexts/request.context';
import {
  getRequestStatus,
  getTableListParams,
  getCSVContent,
} from 'src/selectors/request.selector';
import { actionTypes } from 'src/ducks/request.duck';
import useTabLocation from 'src/hooks/tab-location.hook';
import { getUrlParams } from 'src/libraries/common.library';
import Lang from 'src/libraries/language';
import { ToastSuccess } from 'src/components/atoms/toaster/toaster.component';
import FlightRequestListView from './flight-request-list.view';
import FlightRequestDetail from '../flight-request-detail/flight-request-detail.component';

const checkValue = (value: any): string => {
  if (value !== null && value !== undefined) {
    return value.toString();
  }
  return '';
};

const oneColumnData = (data: any[], field: string): string => {
  const rtrnVl = data.map((value) => {
    return checkValue(value[field]);
  });
  return rtrnVl.toString();
};

const isFlagValue = (flag: any): string => {
  if (flag !== null && flag !== undefined) {
    return flag.toLowerCase() === 'true' ? 'yes' : 'no';
  }
  return '';
};

const FlightRequestList: React.FC = () => {
  const location = useLocation();
  const { state, actions } = useRequestContext();
  const { currentTab } = useTabLocation();
  const [detail, setDetail] = useState({
    requestId: undefined,
    category: undefined,
  });
  // This will prevent the list to be fetch every time we open request details popup
  const [cacheData, setCacheData] = useState('');
  const [dateRange, setDateRange] = useState(
    `${Moment().format('MM/DD/YYYY')} - ${Moment().format('MM/DD/YYYY')}`
  );

  useEffect(() => {
    actions.filterGET();
  }, [actions]);

  useEffect(() => {
    const { requestId, category } = getUrlParams(location.search);

    setDetail({
      requestId,
      category,
    });
  }, [location, setDetail]);

  const handleFetch = useCallback(
    (params: IORGDataTableQueryState, force = false) => {
      const data = getTableListParams(params, currentTab);
      const path = JSON.stringify(data);

      if (cacheData !== path || force) {
        setCacheData(path);
        actions.listGET(data);
      }

      actions.setListPath(`${location.pathname}/${location.search}`);
    },
    [actions, cacheData, setCacheData, currentTab, location]
  );

  const handleCancel = useCallback(
    (msg) => {
      ToastSuccess(msg);
      handleFetch(JSON.parse(cacheData), true);
    },
    [cacheData, handleFetch]
  );

  const handleDownload = useCallback(
    async (params: IORGDataTableQueryState) => {
      const data = getTableListParams(params, currentTab);
      const result = await actions.listGETDownload({
        ...data,
        page: 0,
        limit: 0,
      });
      if (result.payload && result.payload.rows && result.payload.rows.length) {
        const items = result.payload.rows.map((value) => [
          Moment.utc(value.flightDate).startOf('day').format('MM/DD/YYYY'),
          checkValue(value.flightNo),
          checkValue(value.requestorDistrict),
          checkValue(value.fieldCompany),
          checkValue(value.flightCategory),
          checkValue(value.flightType),
          checkValue(value.requestorName),
          checkValue(value.pocNo),
          checkValue(value.pocName),
          checkValue(value.pocPhone),
          checkValue(value.pocEmail),
          checkValue(value.pocManagerName),
          checkValue(value.pocManagerEmail),
          checkValue(value.pocDept),
          checkValue(value.pocJobTitle),
          checkValue(value.pocCompany),
          checkValue(value.operatorName),
          checkValue(value.tailNumber),
          checkValue(value.pilot),
          checkValue(value.flightHours),
          checkValue(value.standByHours),
          checkValue(value.costCenter),
          oneColumnData(value.accounting, 'workOrder'),
          oneColumnData(value.accounting, 'ioNo'),
          oneColumnData(value.accounting, 'planCategory'),
          checkValue(value.flightCost),
          isFlagValue(value.observedFlag),
          checkValue(value.riskScore),
          isFlagValue(value.contractorMissionFlag),
        ]);

        const content = getCSVContent(items, [
          Lang.LBL_ACTUAL_FLIGHT_DATE,
          Lang.LBL_FLIGHT_NO,
          Lang.LBL_DISTRICT_PROJECT,
          Lang.LBL_REQUESTING_DEPARTMENT,
          Lang.LBL_FLIGHT_CATEGORY,
          Lang.LBL_FLIGHT_TYPE,
          Lang.LBL_REQUESTOR,
          Lang.LBL_POC_NO,
          Lang.LBL_POC_NAME,
          Lang.LBL_POC_PHONE,
          Lang.LBL_POC_EMAIL,
          Lang.LBL_POC_MANAGER_NAME,
          Lang.LBL_POC_MANAGER_EMAIL,
          Lang.LBL_POC_DEPT,
          Lang.LBL_POC_TITLE,
          Lang.LBL_POC_COMPANY,
          Lang.LBL_OPERATOR,
          Lang.LBL_TAIL_NUMBER,
          Lang.LBL_FIRE_DISPATCH_PILOT,
          Lang.LBL_REQUEST_FLIGHT_HOURS,
          Lang.LBL_REQUEST_FLIGHT_STANDBY_HOURS,
          Lang.LBL_REQUEST_COST_CENTER,
          Lang.LBL_REQUEST_WORK_ORDER,
          Lang.LBL_REQUEST_IO_NUMBER,
          Lang.LBL_REQUEST_PLAN_CATEGORY,
          Lang.LBL_FLIGHT_COST,
          Lang.LBL_OBSERVED,
          Lang.LBL_RISK_ASSESSMENT_SCORE,
          Lang.LBL_CONTRACTOR_MISSION,
        ]);
        const link = document.createElement('a');
        link.setAttribute('href', content);
        link.setAttribute(
          'download',
          `Aviation_Services_Flight_Report_${Moment().format('YYYYMMDD')}.xls`
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    },
    [currentTab, actions]
  );
  return (
    <>
      <FlightRequestDetail
        requestId={detail.requestId && Number(detail.requestId)}
        type={detail.category}
      />

      <FlightRequestListView
        data={state.list}
        filters={state.filters}
        dateRange={dateRange}
        total={state.total}
        allTotal={state.allTotal}
        passengerTotal={state.passengerTotal}
        constructionTotal={state.constructionTotal}
        droneTotal={state.droneTotal}
        loading={
          getRequestStatus(state, actionTypes.REQUEST_LIST_FETCH).fetching
        }
        downloading={
          getRequestStatus(state, actionTypes.REQUEST_LIST_FETCH_DOWNLOAD)
            .fetching
        }
        handleFetch={handleFetch}
        handleCancel={(value) => handleCancel(value)}
        handleDateRange={(value) => setDateRange(value)}
        handleDownload={handleDownload}
        handleCostStatusSuccess={(value) => handleCancel(value)}
      />
    </>
  );
};

export default FlightRequestList;
